import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';

import { MultiMap } from './common-utils.js';
import {
  ClassTime,
  DayOfWeek,
  DayOfWeekLabelMap,
  LearningPathNames,
  OnboardingSessionConfig,
  OnboardingSessionPlaceholderDay,
  OnboardingSessionType,
  ScheduleBreak,
  type OnboardingLearningPathName,
} from './data/index.js';
import {
  InstantInterval,
  PlainDateInterval,
  PlainDateIntervalSet,
  PlainDateTimeInterval,
  PlainTimeInterval,
} from './temporal-utils/index.js';

export const SessionStatusToLabel = {
  [PrismaEnums.SessionStatus.scheduled]: 'Scheduled',
  [PrismaEnums.SessionStatus.completed]: 'Completed',
  [PrismaEnums.SessionStatus.in_progress]: 'In progress',
  [PrismaEnums.SessionStatus.cancelled]: 'Canceled',
  [PrismaEnums.SessionStatus.archived]: 'Archived',
} as const;

/**
 * Defines the next valid status for each status
 */
export const SessionStatusToNextValidStatus: Record<PrismaEnums.SessionStatus, PrismaEnums.SessionStatus[]> = {
  [PrismaEnums.SessionStatus.scheduled]: [
    PrismaEnums.SessionStatus.in_progress,
    PrismaEnums.SessionStatus.completed,
    PrismaEnums.SessionStatus.cancelled,
    PrismaEnums.SessionStatus.archived,
  ],
  [PrismaEnums.SessionStatus.in_progress]: [
    PrismaEnums.SessionStatus.completed,
    PrismaEnums.SessionStatus.cancelled,
    PrismaEnums.SessionStatus.scheduled,
    PrismaEnums.SessionStatus.archived,
  ],
  [PrismaEnums.SessionStatus.completed]: [
    PrismaEnums.SessionStatus.in_progress,
    PrismaEnums.SessionStatus.cancelled,
    PrismaEnums.SessionStatus.archived,
  ],
  [PrismaEnums.SessionStatus.cancelled]: [PrismaEnums.SessionStatus.scheduled, PrismaEnums.SessionStatus.archived],
  [PrismaEnums.SessionStatus.archived]: [],
};

/**
 * Determines if a session can move to a certain status based on its current status.
 *
 * @param {SessionStatus | { status: SessionStatus }} currentSessionOrStatus - The current status of the session or an object containing the session status.
 * @param {SessionStatus} nextStatus - The status the session is trying to move to.
 * @return {boolean} Returns true if the session can move to the next status, false otherwise.
 */
export function isSessionStatusMoveValid(
  currentSessionOrStatus: PrismaEnums.SessionStatus | { status: PrismaEnums.SessionStatus },
  nextStatus: PrismaEnums.SessionStatus,
): boolean {
  const currentStatus =
    typeof currentSessionOrStatus === 'string' ? currentSessionOrStatus : currentSessionOrStatus.status;
  return currentStatus === nextStatus || SessionStatusToNextValidStatus[currentStatus].includes(nextStatus);
}

const temporalDayOfWeekToEnum = {
  1: DayOfWeek.monday,
  2: DayOfWeek.tuesday,
  3: DayOfWeek.wednesday,
  4: DayOfWeek.thursday,
  5: DayOfWeek.friday,
  6: DayOfWeek.saturday,
  7: DayOfWeek.sunday,
} as const;

export function dayOfWeekFromTemporal({ dayOfWeek }: Temporal.PlainDate): DayOfWeek {
  return temporalDayOfWeekToEnum[dayOfWeek as keyof typeof temporalDayOfWeekToEnum]!;
}

export function dateIntervalFromScheduleBreak({ startPlainDate, endPlainDate }: ScheduleBreak): PlainDateInterval {
  return PlainDateInterval.from(startPlainDate, endPlainDate);
}

export function* dateTimeIntervalsFromClassTime({
  classTime,
  overrideStartPlainDate,
  overrideEndPlainDate,
}: {
  classTime: ClassTime;
  overrideStartPlainDate?: Temporal.PlainDate;
  overrideEndPlainDate?: Temporal.PlainDate;
}): IterableIterator<PlainDateTimeInterval> {
  const {
    schedule: { startPlainDate, endPlainDate, scheduleBreaks },
    meetingBlocks,
  } = classTime;
  const scheduleInterval = new PlainDateInterval(
    overrideStartPlainDate ?? startPlainDate,
    overrideEndPlainDate ?? endPlainDate,
  );

  if (classTime.type === 'one_time') {
    // for one-time class time, we don't care about iterating over all the actual dayOfWeek
    // We just need the meeting time from the placeholder day 'OnboardingSessionPlaceholderDay'
    const meetingBlock = classTime.meetingBlocks.find((mb) => mb.day === OnboardingSessionPlaceholderDay);
    if (!meetingBlock) {
      return;
    }

    yield new PlainTimeInterval(meetingBlock.startPlainTime, meetingBlock.endPlainTime).withPlainDate(
      scheduleInterval.start, // one day only, does not need end date
    );
    return;
  }

  const breaksSet = new PlainDateIntervalSet(scheduleBreaks.map(dateIntervalFromScheduleBreak));
  const meetingBlocksByDay = MultiMap.groupBy(meetingBlocks, (b) => b.day);

  // For each day that falls under the schedule (inclusive both ends)
  for (const date of scheduleInterval) {
    // If the day does not fall under a known break
    if (!breaksSet.contains(date)) {
      const dayOfWeek = dayOfWeekFromTemporal(date);
      const meetingBlocks = meetingBlocksByDay.get(dayOfWeek) ?? [];
      for (const { startPlainTime, endPlainTime } of meetingBlocks) {
        yield new PlainTimeInterval(startPlainTime, endPlainTime).withPlainDate(date);
      }
    }
  }
}

export function* instantIntervalsFromClassTime({
  classTime,
  overrideStartPlainDate,
  overrideEndPlainDate,
}: {
  classTime: ClassTime;
  overrideStartPlainDate?: Temporal.PlainDate;
  overrideEndPlainDate?: Temporal.PlainDate;
}): IterableIterator<InstantInterval> {
  const { schedule } = classTime;
  const { timeZone } = schedule;
  for (const interval of dateTimeIntervalsFromClassTime({
    classTime,
    overrideStartPlainDate,
    overrideEndPlainDate,
  })) {
    yield interval.toInstantInterval(timeZone);
  }
}

/**
 * For one-time lesson (diagnostic, practice and practice&diagnostic), there are fixed lesson names
 * for each type. This function returns the lesson name based on the type of the lesson.
 * Mapping defined in https://airreading.atlassian.net/browse/AIR-155
 *
 * @param type The type of the one-time lesson
 */
export function getOnboardingSessionConfig(type: OnboardingSessionType): OnboardingSessionConfig {
  if (type === 'diagnostic') {
    return {
      lessonName: 'Diagnostic',
      learningPathName: LearningPathNames.Diagnostic,
      lessonType: 'diagnostic',
      groupSize: 2,
      scheduleDateKey: 'diagnosticPlainDate',
    };
  }
  if (type === 'practice') {
    return {
      lessonName: 'Practice (Zoom)',
      learningPathName: LearningPathNames['Practice (Zoom)'],
      lessonType: 'practice',
      groupSize: 20,
      scheduleDateKey: 'practicePlainDate',
    };
  }

  if (type === 'practice_google_meet') {
    return {
      lessonName: 'Practice (Google Meet)',
      learningPathName: LearningPathNames['Practice (Google Meet)'],
      lessonType: 'practice',
      groupSize: 20,
      scheduleDateKey: 'practicePlainDate',
    };
  }

  if (type === 'practice_and_diagnostic') {
    return {
      lessonName: 'Diagnostic w/ Practice (Zoom)',
      learningPathName: LearningPathNames['Diagnostic w/ Practice (Zoom)'],
      lessonType: 'practice_and_diagnostic',
      groupSize: 2,
      scheduleDateKey: 'diagnosticPlainDate',
    };
  }

  if (type === 'practice_and_diagnostic_google_meet') {
    return {
      lessonName: 'Diagnostic w/ Practice (Google Meet)',
      learningPathName: LearningPathNames['Diagnostic w/ Practice (Google Meet)'],
      lessonType: 'practice_and_diagnostic',
      groupSize: 2,
      scheduleDateKey: 'diagnosticPlainDate',
    };
  }

  if (type === 'makeup_diagnostic') {
    return {
      lessonName: 'Makeup Diagnostic 1-1',
      learningPathName: LearningPathNames['Makeup Diagnostic'],
      lessonType: 'makeup_diagnostic',
      groupSize: 3,
      scheduleDateKey: 'diagnosticPlainDate', // not really used.
      maxSessionsByRange: 5,
    };
  }

  throw new Error(`Unknown lesson type: ${type}`);
}

export const OneTimeOnboardingLearningPathNames: readonly OnboardingLearningPathName[] = [
  LearningPathNames['Practice (Zoom)'],
  LearningPathNames.Diagnostic,
  LearningPathNames['Diagnostic w/ Practice (Zoom)'],
  LearningPathNames['Practice (Google Meet)'],
  LearningPathNames['Diagnostic w/ Practice (Google Meet)'],
];

export const InvalidStartingLearningPathNames: readonly OnboardingLearningPathName[] = [
  ...OneTimeOnboardingLearningPathNames,
  LearningPathNames['Makeup Diagnostic'],
];

export function nextPlainDateOnDayOfWeek(today: Temporal.PlainDate, targetDay: DayOfWeek): Temporal.PlainDate {
  const currentDay = today.dayOfWeek % 7;
  const targetDayNumber = DayOfWeekLabelMap[targetDay].day;
  const daysUntilTarget = (targetDayNumber + 7 - currentDay) % 7 || 7;
  return today.add({ days: daysUntilTarget });
}

export const ProgressMonitorLessonNameSuffix = '(PM)';
